import { useActor, useSelector } from "@xstate/react";
import {
  BundlePickerApiRequirements,
  bundlePickerMachine,
  sumExtraPrice,
} from "./BundlePickerMachineV3";
import {
  ActivePeriod,
  Bundle,
  BundleLine,
  BundleLineVariant,
  BundleLineVariantId,
  BundlePrice,
  getPrice,
  ImageAsset,
  Product,
  ProductId,
  SpecialPricePeriod,
  sumAdditionalPrice,
  TableId,
  unwrapPrice,
} from "@greeter/core";
import { useEffect, useMemo } from "react";
import { useCartActor } from "../../Cart/CartProvider";
import { motion, AnimatePresence, LayoutGroup } from "motion/react";
import { nanoid } from "nanoid";
import { classNames } from "@greeter/util";
import { combine, dropInOutFromBottom } from "@greeter/motion";
import { formatMoney } from "@greeter/formatters";
import { IconLock, IconPencil } from "@tabler/icons-react";
import { PickerOverviewDisplay } from "./PickerOverviewDisplay";
import { AddToCartButton } from "./AddToCartButton";
import { useIonAlert } from "@ionic/react";
import { useToggle } from "@greeter/matter";
import { Card, CardProps } from "@greeter/matter-v2";
import {
  AccordionProvider,
  BundleLineVariantPicker,
  BundleLineVariantPickerProps,
  OnChange,
} from "./BundleLineVariantPicker";
import { ToastContainer } from "react-toastify";
import { Toolbar } from "./Toolbar";
import { AlmostThereModal } from "./AlmostThereModal";
import {
  bundleHasChanges,
  bundleIsValid,
  bundleLineLimitExceeded,
  bundleLineLimitReached,
  bundleLineLocked,
  bundleSumAdditionalPrice,
  bundleSumTotalPrice,
} from "./util";
import { EditTablesModal } from "./EditTablesModal";
import { VariantLibraryModal } from "./VariantLibraryModal";

import css from "./BundlePickerV3.module.scss";
import { Portal } from "./Portal";
import { Log } from "@greeter/log";
import { BundlePickerV3Provider } from "./BundlePickerV3Provider";
import { BundleForm } from "../../models";
import { uniqBy } from "lodash";

BundleLineCollectionPicker.LogTag = "[BundleLineCollectionPicker]";
function BundleLineCollectionPicker({
  onChange,
  ...props
}: {
  bundle: BundleForm;
  index?: Map<BundleLineVariantId, BundleLineVariant>;
  indexedProducts?: Map<ProductId, Product>;
  indexedBundleLinesByVariants?: Map<BundleLineVariantId, BundleLine>;
  onChange?: OnChange;
  onPickNewVariant?: (data: { bundleLineId: string }) => void;
  onPickDifferentVariant?: BundleLineVariantPickerProps["onPickDifferentVariant"];

  layoutId?: string;
  style?: React.CSSProperties;
}) {
  return (
    <AccordionProvider>
      <LayoutGroup>
        <motion.div
          className={css.BundleLineCollectionPicker}
          key={"pickingVariants_" + props.bundle.id}
          {...combine(dropInOutFromBottom, { transition: { duration: 0.1 } })}
          style={props.style}
          layoutId={props.layoutId}
        >
          {props.bundle.lines.map((l) => {
            return (
              <BundleLineVariantPicker
                key={l.id}
                data-testid={`bundleline-variant-picker-${l.id}`}
                bundleLine={l}
                indexedProducts={props.indexedProducts}
                // @ts-ignore
                onChange={onChange}
                onPickNewVariant={() =>
                  props.onPickNewVariant?.({ bundleLineId: l.id })
                }
                onPickDifferentVariant={props.onPickDifferentVariant}
              />
            );
          })}
        </motion.div>
      </LayoutGroup>
    </AccordionProvider>
  );
}

function BundlePriceDisplay(props: {
  price: BundlePrice;
  additionalPrice: number;
}) {
  return (
    <div className={css.Price}>
      {props.price.type === "discounted" ? (
        <>
          <s>{formatMoney(props.price.price + props.additionalPrice)}</s>{" "}
          {formatMoney(props.price.discountedPrice + props.additionalPrice)} kr.
        </>
      ) : (
        <>{formatMoney(props.price.price + props.additionalPrice)} kr.</>
      )}
    </div>
  );
}

export type BundleItemProps = {
  bundle: Bundle;
  onClick?: () => void;
  specialPricePeriods?: Array<SpecialPricePeriod>;
  forDate?: Date;
};
export function BundleItem(props: BundleItemProps) {
  const bundlePrice = useMemo(
    () =>
      getPrice(props.bundle, {
        specialPricePeriods: props.specialPricePeriods,
        forDate: props.forDate,
      }),
    [props.bundle, props.specialPricePeriods, props.forDate]
  );

  const lines = useMemo(() => {
    const result: Array<string> = [];
    for (let i = 0; i < 3; i++) {
      const line = props.bundle.lines[i];

      if (line) result.push(`${line.quantity} x ${line.name}`);
      else result.push("");
    }
    return result;
  }, [props.bundle.lines]);

  return (
    <motion.div
      layout
      className={css.BundleItem}
      style={{ borderRadius: 10 }}
      onClick={props.onClick}
      data-testid={`bundle-item-${props.bundle.id}`}
    >
      <div className={css.Content}>
        <div className={css.Title}>{props.bundle.title}</div>
        <div className={css.ContentLines}>
          {lines.map((line) => {
            return (
              <div key={nanoid()} className={css.ContentLine}>
                {line}
              </div>
            );
          })}
          {props.bundle.lines.length > 3 ? (
            <div className={css.ContentLine}>...</div>
          ) : (
            <div className={css.ContentLine} />
          )}
        </div>
        <BundlePriceDisplay
          price={bundlePrice}
          additionalPrice={bundleSumAdditionalPrice(props.bundle)}
        />
      </div>

      <div className={css.Cover} style={{ height: "100%" }}>
        <img
          src={
            props.bundle.coverAsset
              ? ImageAsset.findUriWithSizeOrDefault(
                  props.bundle.coverAsset,
                  "16x9-w512"
                )
              : props.bundle.coverUrl ?? ""
          }
          className="cover w-full"
        />
      </div>
    </motion.div>
  );
}

function filterActiveBundles(
  bundles: Array<Bundle>,
  activePeriods: Map<ProductId, Array<ActivePeriod>>,
  forDate: Date
) {
  const result: Array<Bundle> = [];

  for (let i = 0, l = bundles.length; i < l; i++) {
    const bundle = bundles[i];
    const activePeriodsForBundle = activePeriods.get(bundle.id);

    if (!activePeriodsForBundle || activePeriodsForBundle.length <= 0) {
      result.push(bundle);
      continue;
    }

    for (let j = 0, l = activePeriodsForBundle.length; j < l; j++) {
      const activePeriod = activePeriodsForBundle[j];

      if (
        activePeriod.period.from <= forDate &&
        activePeriod.period.to >= forDate
      ) {
        result.push(bundle);
      }
    }
  }

  return result;
}

function EditBundleButton(props: Pick<CardProps, "onClick">) {
  return (
    <Card
      variant="regular"
      act="button"
      {...props}
      data-testid={`btn-edit-bundle`}
    >
      <Card.Content
        padding="sm"
        preset="flex-aligned-center"
        style={{
          paddingTop: 10,
          paddingBottom: 10,
          fontSize: 14,
        }}
      >
        <IconPencil
          size={20}
          vectorEffect="non-scaling-stroke"
          style={{ strokeWidth: 1 }}
        />{" "}
        Ændre bordpakken
      </Card.Content>
    </Card>
  );
}

function EditTablesButton(props: Pick<CardProps, "onClick">) {
  return (
    <Card act="button" style={{ height: "100%" }} {...props}>
      <Card.Content
        preset="flex-aligned-center"
        style={{
          fontSize: 12,
        }}
      >
        <IconPencil
          size={15}
          style={{ strokeWidth: 1 }}
          vectorEffect="non-scaling-stroke"
        />{" "}
        Skift bord
      </Card.Content>
    </Card>
  );
}

export type BundlePickerV3Props = {
  venueId: string;
  api: BundlePickerApiRequirements;

  forDateAndTime?: Date;
  specialPricePeriods?: Map<ProductId, Array<SpecialPricePeriod>>;
  activePeriods?: Map<ProductId, Array<ActivePeriod>>;

  /**
   * The price necessary to fill up the min. price of the tables selected
   */
  minPrice: number;

  onDismiss?: () => void;
  onBundlePicked?: (data: { title?: string }) => void;

  onChangeTables?: () => void;
  tables?: Array<TableId>;

  onBundleChange?: (b: Bundle) => void;

  slots?: EditTablesModal["slots"];
};

export function BundlePickerV3(props: BundlePickerV3Props) {
  const cart = useCartActor();
  const cartTotal = useSelector(cart, (s) => s.context.total);

  const currentTotal = useSelector(cart, (s) => s.context.total);
  const [snap, send, m] = useActor(bundlePickerMachine, {
    input: {
      venueId: props.venueId,
      api: props.api,
      cartRef: cart,
      forDate: props.forDateAndTime,
    },
  });
  useEffect(
    () => send({ type: "setForDate", data: props.forDateAndTime }),
    [props.forDateAndTime]
  );
  const bundles = useSelector(m, (s) => s.context.bundles);
  const bundleId = useSelector(m, (s) => s.context.bundleId);
  const bundle = useSelector(m, (s) =>
    s.context.bundleId
      ? s.context.bundles.get(s.context.bundleId) ||
        s.context.ticketEventsBundles?.get(s.context.bundleId ?? "")
      : undefined
  );
  const bundleLinesByVariantLines = useSelector(
    m,
    (s) => s.context.bundleLinesByVariants
  );
  const bundleLines = useSelector(m, (s) => s.context.bundleLines);
  const changedBundle = useSelector(m, (s) => s.context.bundleChanges?.value);
  const bundleLineVariants = useSelector(
    m,
    (s) => s.context.bundleLineVariants
  );
  const variantProducts = useSelector(m, (s) => s.context.variantProducts);

  const pickingFor = useSelector(m, (s) => s.context.pickingFor);

  const ticketEventBundles = useSelector(
    m,
    (s) => s.context.ticketEventsBundles
  );

  function getBundleLine() {
    return pickingFor?.variantId
      ? bundleLinesByVariantLines.get(pickingFor.variantId)
      : pickingFor?.bundleLineId
      ? bundleLines.get(pickingFor.bundleLineId)
      : undefined;
  }
  const bundleLine = getBundleLine();

  const venue = useSelector(m, (s) => s.context.venue);
  const activeBundles = useMemo(() => {
    if (!bundles) return [];
    if (ticketEventBundles.size > 0)
      return Array.from(ticketEventBundles.values());

    const publicBundles = Array.from(bundles.values()).filter(
      (b) => b.isPublic
    );
    let result =
      props.activePeriods && props.forDateAndTime
        ? Product.sortByPriceAndPriority(
            filterActiveBundles(
              publicBundles,
              props.activePeriods,
              props.forDateAndTime
            ),
            {
              specialPricePeriods: props.specialPricePeriods,
              forDate: props.forDateAndTime ?? new Date(),
            }
          )
        : Product.sortByPriceAndPriority(publicBundles, {
            specialPricePeriods: props.specialPricePeriods,
            forDate: props.forDateAndTime ?? new Date(),
          });

    result = uniqBy(result, (b) => b.id);

    return result;
  }, [bundles, ticketEventBundles, props.activePeriods, props.forDateAndTime]);

  const headless = snap.matches({ ui: "pickingVariants" });

  const bundlePrice = useSelector(m, (s) => s.context.bundlePrice);

  const [isPickerOverviewOpen, togglePickerOverviewOpen] = useToggle(false);

  useEffect(() => {
    if (changedBundle) {
      props.onBundleChange?.(changedBundle);
    }
  }, [changedBundle]);

  useEffect(
    () =>
      m.on("bundlePicked", ({ data }) =>
        props.onBundlePicked?.({ title: data.title })
      ).unsubscribe,
    [m, props.onBundlePicked]
  );

  const [alert] = useIonAlert();

  const changedBundleLine = changedBundle?.lines.find(
    (l) => l.id === bundleLine?.id
  );

  return (
    <BundlePickerV3Provider
      featureFlags={{
        "upsell.v1.guest":
          snap.context.upsellFeatureFlagRef.getSnapshot().context.data
            ?.active ?? false,
      }}
    >
      <div
        {...classNames(css.BundlePicker, headless && css.LayoutHeadless)}
        style={
          {
            backgroundColor: "var(--gm-color-default-bg)",
          } as React.CSSProperties
        }
      >
        <Toolbar
          title={
            snap.matches({ ui: "pickingBundle" })
              ? "Vælg bordpakke"
              : snap.matches({ ui: "pickingStrategy" })
              ? "Tilpas eller fortsæt"
              : snap.matches({ ui: "pickingVariants" })
              ? `Ændre ${bundle?.title}`
              : "Tilpas bordpakke"
          }
          onBack={() => {
            const hasChanges =
              bundle &&
              changedBundle &&
              bundleHasChanges(bundle, changedBundle);
            if (snap.matches({ ui: "pickingBundle" }) && props.onDismiss) {
              props.onDismiss?.();
            } else if (isPickerOverviewOpen) {
              togglePickerOverviewOpen();
            } else if (hasChanges) {
              alert({
                mode: "ios",
                message: "Hvis du går tilbage vil du miste dine ændringer",
                buttons: [
                  { text: "Bliv" },
                  {
                    text: "Gå tilbage",
                    handler() {
                      send({ type: "prev" });
                    },
                  },
                ],
              });
            } else {
              send({ type: "prev" });
            }
          }}
        />

        <motion.div className={css.Header} layout>
          <AnimatePresence mode="popLayout">
            {snap.matches({ ui: "pickingBundle" }) && (
              <motion.div
                key="pickingBundleHeader"
                transition={{ ease: "easeOut" }}
                layout="position"
                style={{ overflow: "hidden", height: "100%" }}
                {...dropInOutFromBottom}
              >
                <motion.img
                  className={`cover w-full ${css.Cover}`}
                  style={{ aspectRatio: "16 / 6" }}
                  src={venue?.coverAsset.uri}
                />
                <motion.h1 className={css.SectionTitle}>
                  Vælg bordpakke
                </motion.h1>
              </motion.div>
            )}

            {snap.matches({ ui: "pickingStrategy" }) && (
              <motion.div
                key="pickingVariantHeader"
                layout="position"
                transition={{ ease: "easeOut", duration: 0.2 }}
                style={{ overflow: "hidden", height: "100%" }}
                {...dropInOutFromBottom}
              >
                <motion.img
                  className={`cover w-full ${css.Cover}`}
                  src={bundles.get(bundleId!)?.coverAsset?.uri}
                />
                {snap.matches({ ui: "pickingStrategy" }) && (
                  <motion.h1 className={css.SectionTitle}>
                    {bundles.get(bundleId!)?.title}
                  </motion.h1>
                )}
                {snap.matches({ ui: "pickingVariants" }) && (
                  <motion.h1 className={css.SectionTitle}>
                    Vælg indhold
                  </motion.h1>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <div
          className={css.Content}
          style={{
            position: "relative",
            padding: "1rem",
            // Used when the bottom bar was there. It isn't any longer
            // Kept for legacy reasons until it's certain it's not longer necessary
            // padding:
            //   "1rem 1rem calc(var(--bottom-bar-height, 4rem) + 1rem + 5rem)",
          }}
        >
          <AnimatePresence mode="popLayout">
            {snap.matches({ ui: "pickingBundle" }) && (
              <motion.div
                layout="position"
                key="pickingBundle"
                className={css.BundlePickerContainer}
                {...dropInOutFromBottom}
              >
                {activeBundles?.map((b) => {
                  return (
                    <BundleItem
                      key={b.id}
                      bundle={b}
                      specialPricePeriods={snap.context.specialPricePeriods.get(
                        b.id
                      )}
                      forDate={props.forDateAndTime}
                      onClick={() => send({ type: "pickBundle", data: b.id })}
                    />
                  );
                })}
              </motion.div>
            )}

            {snap.matches({ ui: "pickingStrategy" }) && bundle && (
              <motion.div
                layout="position"
                key={"pickingStrategy_" + bundle.id}
                className={css.BundlePreview}
                {...dropInOutFromBottom}
              >
                <div className={css.Lines}>
                  {bundle?.lines.map((l) => {
                    const locked = bundleLineLocked(l);
                    return (
                      <div className={css.Line}>
                        <h4 className={css.LineTitle}>
                          {locked && <span>{l.quantity} x </span>}
                          {l.name}
                          {locked && (
                            <>
                              {" "}
                              <IconLock size={20} />
                            </>
                          )}
                        </h4>
                        {!locked &&
                          l.variants
                            .filter((v) => (v.quantity ?? 0) > 0)
                            .map((v) => {
                              return (
                                <p className={css.QuantityLine}>
                                  {v.quantity} x {v.name}
                                </p>
                              );
                            })}
                      </div>
                    );
                  })}
                  {bundle && (
                    <BundlePriceDisplay
                      price={getPrice(bundle, {
                        forDate: props.forDateAndTime,
                        specialPricePeriods:
                          snap.context.specialPricePeriods.get(bundle.id),
                      })}
                      additionalPrice={bundleSumAdditionalPrice(bundle)}
                    />
                  )}
                </div>
                <div className={css.ButtonContainer}>
                  <EditBundleButton
                    onClick={() => send({ type: "pickManually" })}
                  />
                  <AddToCartButton
                    onClick={() => {
                      if (bundleIsValid(bundle)) {
                        send({ type: "pickAutomatically" });
                        return;
                      }

                      send({ type: "pickManually" });
                    }}
                  />
                </div>
              </motion.div>
            )}

            {snap.matches({ ui: "pickingVariants" }) && changedBundle && (
              <motion.div
                key="editBundleView"
                layout
                style={{ position: "relative" }}
              >
                <BundleLineCollectionPicker
                  bundle={changedBundle}
                  index={bundleLineVariants}
                  indexedProducts={variantProducts}
                  indexedBundleLinesByVariants={bundleLinesByVariantLines}
                  onPickNewVariant={(data) =>
                    send({ type: "openVariantLibrary", data })
                  }
                  onPickDifferentVariant={(data) =>
                    send({ type: "openVariantLibrary", data })
                  }
                  onChange={(data) =>
                    send({
                      type: "changeBundle",
                      data: {
                        type: "change",
                        data,
                      },
                    })
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>

          {bundle &&
            changedBundle &&
            snap.matches({ ui: "pickingVariants" }) && (
              <Portal selector="#bundle-picker-action-bar">
                <motion.div
                  layout
                  key="pickerOverviewDisplay"
                  {...classNames(
                    css.BottomBarActionsContainer,
                    isPickerOverviewOpen && css.Open
                  )}
                  {...dropInOutFromBottom}
                >
                  <motion.div layout className={css.BottomBarActions}>
                    <PickerOverviewDisplay
                      open={isPickerOverviewOpen}
                      onToggle={togglePickerOverviewOpen}
                      originalBundle={bundle}
                      bundle={changedBundle}
                      minPrice={props.minPrice}
                      bundleAdditionalCosts={
                        sumAdditionalPrice(changedBundle) +
                        sumExtraPrice(changedBundle)
                      }
                      bundleOriginalPrice={unwrapPrice(getPrice(changedBundle))}
                      cartTotal={cartTotal}
                    />
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: 5,
                      }}
                    >
                      <EditTablesButton
                        onClick={() => send({ type: "openEditTables" })}
                      />
                      <AddToCartButton
                        disabled={!bundleIsValid(changedBundle)}
                        total={bundlePrice}
                        onClick={() => {
                          if (
                            bundleSumTotalPrice(changedBundle) < props.minPrice
                          ) {
                            send({ type: "openAlmostThere" });
                          } else {
                            send({ type: "finishPicking" });
                          }
                        }}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              </Portal>
            )}

          <AlmostThereModal
            isOpen={snap.matches({ modal: { almostThere: "open" } })}
            onBack={() => send({ type: "closeAlmostThere" })}
            onChangeTables={() => send({ type: "openEditTables" })}
            onContinue={() => send({ type: "finishPicking" })}
            onChangeQuantity={(q) =>
              send({ type: "setQuantity", data: { quantity: q } })
            }
            quantity={snap.context.quantity ?? 1}
            bundle={changedBundle}
            minPrice={props.minPrice}
          />

          <EditTablesModal
            isOpen={snap.matches({ modal: { editTables: "open" } })}
            onBack={() => send({ type: "closeEditTables" })}
            minPrice={props.minPrice}
            currentTotal={currentTotal}
            slots={props.slots}
          />

          <VariantLibraryModal
            isOpen={snap.matches({ modal: { variantLibrary: "open" } })}
            products={Array.from(variantProducts.values())}
            categories={Array.from(snap.context.categories.values())}
            pickingFor={pickingFor}
            bundleLine={bundleLine}
            priceDisplay={
              // Adding a new. Therefor show price
              (changedBundleLine &&
                bundleLineLimitReached(changedBundleLine) &&
                pickingFor?.bundleLineId) ||
              // Already exceeded, therefore show price
              (changedBundleLine && bundleLineLimitExceeded(changedBundleLine))
                ? "salesPrice"
                : // Else we assume it's not exceeded
                  "additionalPrice"
            }
            onPick={(id) => {
              send({
                type: "changeBundle",
                data: { type: "pick", data: { variantId: id } },
              });
              send({ type: "closeVariantLibrary" });
            }}
            onClose={() => send({ type: "closeVariantLibrary" })}
          />
        </div>
        <ToastContainer toastClassName={css.Toast} />
      </div>
    </BundlePickerV3Provider>
  );
}
